import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Cpu,
  Sparkles,
  WashingMachine,
  Car,
  Megaphone,
  ShieldCheck,
  Droplets,
  Sun,
  Thermometer,
  Factory,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const BASE_URL = "https://www.naklejki-3d.com.pl";
const URL = `${BASE_URL}/zastosowania-branzowe/`;

interface Industry {
  id: string;
  icon: typeof Cpu;
  h2: string;
  lead: string;
  body: string[];
  applications: string[];
  benefits: string[];
  keywords: string[];
  ctaLabel: string;
  ctaHref: string;
}

const INDUSTRIES: Industry[] = [
  {
    id: "elektronika",
    icon: Cpu,
    h2: "Naklejki 3D dla producentów elektroniki",
    lead:
      "Naklejki 3D dla elektroniki to trwałe, wypukłe etykiety zalewane żywicą poliuretanową, stosowane do oznaczania obudów, paneli sterowania i akcesoriów RTV, IT oraz smart home.",
    body: [
      "W branży elektronicznej etykieta pełni dwie role jednocześnie: jest nośnikiem marki i elementem informacyjnym urządzenia. Naklejki żywiczne łączą obie funkcje — soczewka z żywicy poliuretanowej tworzy efekt szkła, który podnosi postrzeganą jakość produktu, a jednocześnie stanowi warstwę ochronną dla nadruku. Nadruk znajduje się pod żywicą, więc nie ściera się przy codziennym użytkowaniu, czyszczeniu obudowy ani kontakcie z kablami i akcesoriami.",
      "Jako producent naklejek 3D realizujemy serie OEM dla firm elektronicznych: od kilkuset sztuk prototypowych po wielotysięczne partie produkcyjne z pełną powtarzalnością koloru. Naklejki 3D na produkty elektroniczne wycinamy w dowolnym kształcie — okrągłe, prostokątne z zaokrągleniami, konturowe wokół logotypu, a także w formie długich pasków na panele sterowania i listwy z oznaczeniami funkcji.",
      "Etykiety 3D dla przemysłu elektronicznego dobieramy pod materiał obudowy: ABS, poliwęglan, aluminium szczotkowane, stal lakierowana proszkowo czy tworzywa z domieszką gumy (soft-touch). Do każdej z tych powierzchni stosujemy inny klej akrylowy o odpowiedniej sile początkowej i końcowej, co eliminuje odklejanie się krawędzi po kilku miesiącach eksploatacji.",
    ],
    applications: [
      "Logotypy na urządzeniach elektronicznych — RTV, IT, smart home",
      "Panele sterowania i oznaczenia funkcji (piktogramy, opisy przycisków)",
      "Naklejki na obudowy sprzętu: routery, sterowniki, moduły, zasilacze",
      "Etykiety informacyjne i serwisowe (numery seryjne, wersje firmware, plomby)",
      "Branding premium dla produktów OEM i white-label",
      "Naklejki 3D na akcesoria elektroniczne: ładowarki, powerbanki, głośniki, słuchawki",
    ],
    benefits: [
      "Odporność na temperaturę pracy urządzeń i wielokrotne ścieranie",
      "Trwałość 5–7 lat bez utraty połysku i czytelności nadruku",
      "Wysoka jakość wizualna i charakterystyczny efekt szkła (soczewka 3D)",
      "Idealne do urządzeń intensywnie użytkowanych i często dotykanych",
      "Odporność na wilgoć, kurz i środki czyszczące stosowane w serwisie",
      "Produkcja w dowolnym kształcie i rozmiarze — od 5 mm do 380 × 280 mm",
    ],
    keywords: [
      "naklejki 3D elektronika",
      "naklejki żywiczne na urządzenia",
      "etykiety 3D dla producentów elektroniki",
      "doming elektronika",
      "naklejki wypukłe elektronika",
      "naklejki 3D na sprzęt elektroniczny",
    ],
    ctaLabel: "Skontaktuj się po wycenę dla branży elektronicznej",
    ctaHref: "/kontakt",
  },
  {
    id: "kosmetyki",
    icon: Sparkles,
    h2: "Naklejki 3D dla kosmetyków",
    lead:
      "Etykiety wypukłe premium na kosmetyki to naklejki zalewane żywicą, które nadają słoiczkom, butelkom i tubom wygląd produktu z najwyższej półki i wytrzymują kontakt z olejami, alkoholem i kremami.",
    body: [
      "W kosmetyce decyzja zakupowa zapada na półce, w kilka sekund. Wypukła etykieta 3D zmienia sposób, w jaki klient odbiera opakowanie: soczewka żywiczna łapie światło, pogłębia kolory i tworzy wrażenie szkliwa, którego nie da się uzyskać zwykłym nadrukiem. To najtańszy sposób na podniesienie postrzeganej wartości produktu bez zmiany całej linii opakowaniowej.",
      "Naklejki żywiczne dla marek beauty produkujemy z folii białej, transparentnej, chromowanej złotej i srebrnej, holograficznej oraz z folii z efektem satyny. Transparentna baza pozwala uzyskać efekt „unoszącego się” logotypu na szklanym flakonie perfum, chrom sprawdza się w liniach limitowanych, a folia biała daje najwierniejsze odwzorowanie kolorów Pantone z brandbooka.",
      "Doming kosmetyczny jest odporny na to, co dzieje się z produktem w łazience: parę wodną, zachlapania, kontakt z zawartością opakowania i wielokrotne wycieranie. Żywica poliuretanowa nie żółknie pod wpływem światła (w przeciwieństwie do żywic epoksydowych bez filtra UV), więc etykieta na kremie po roku wygląda tak samo jak w dniu pakowania.",
    ],
    applications: [
      "Etykiety na kosmetyki: kremy, balsamy, serum, perfumy, olejki",
      "Logotypy na opakowaniach jednostkowych i zbiorczych",
      "Naklejki na słoiczki, butelki, tuby, pompki i airless",
      "Oznaczenia serii premium i edycji limitowanych",
      "Naklejki 3D na zestawy prezentowe i pudełka ozdobne",
      "Etykiety 3D dla marek beauty, salonów SPA i gabinetów kosmetycznych",
    ],
    benefits: [
      "Efekt szkła i prestiżowy, „butikowy” wygląd opakowania",
      "Odporność na wilgoć, chemię i bezpośredni kontakt z kosmetykiem",
      "Wymierny wzrost postrzeganej wartości produktu na półce",
      "Rozwiązanie dopasowane do marek premium i małych serii butikowych",
      "Pełne dopasowanie kolorystyki (Pantone/CMYK), poziomu połysku i wysokości soczewki",
      "Możliwość produkcji krótkich serii pod nowe warianty i testy rynkowe",
    ],
    keywords: [
      "naklejki 3D kosmetyki",
      "etykiety wypukłe na kosmetyki",
      "naklejki żywiczne dla marek beauty",
      "doming kosmetyczny",
      "naklejki 3D na opakowania kosmetyczne",
    ],
    ctaLabel: "Zamów naklejki 3D dla branży kosmetycznej",
    ctaHref: "/kalkulator",
  },
  {
    id: "agd",
    icon: WashingMachine,
    h2: "Naklejki 3D dla AGD",
    lead:
      "Naklejki 3D dla AGD to wypukłe etykiety żywiczne na panele sterowania, oznaczenia funkcji i logotypy producentów sprzętu domowego — odporne na detergenty, wilgoć i codzienne dotykanie.",
    body: [
      "Sprzęt AGD pracuje w najtrudniejszym środowisku, jakie można sobie wyobrazić dla etykiety: para wodna, tłuszcz, detergenty, wysoka temperatura obudowy i setki dotknięć tygodniowo. Zwykły nadruk cyfrowy na folii ściera się w tych warunkach w kilka miesięcy — nadruk zabezpieczony soczewką żywiczną pozostaje czytelny przez cały cykl życia urządzenia.",
      "Naklejki wypukłe na urządzenia AGD produkujemy z uwzględnieniem czytelności piktogramów: dobieramy grubość linii, kontrast i wielkość elementów tak, aby soczewka nie rozmywała drobnych detali. Przy panelach sterowania stosujemy niższy profil żywicy, który nie przeszkadza w obsłudze przycisków i nie zbiera brudu na krawędziach.",
      "Realizujemy serie OEM dla producentów i importerów AGD, w tym wersje językowe tego samego panelu na różne rynki UE. Dzięki cyfrowemu przygotowaniu produkcji zmiana języka opisów nie wymaga nowych narzędzi — wycinamy ten sam kształt, zmieniając wyłącznie plik nadruku.",
    ],
    applications: [
      "Panele sterowania z opisami funkcji i skalą pracy",
      "Oznaczenia funkcji, piktogramy, symbole programów",
      "Logotypy producentów AGD na froncie i bokach urządzeń",
      "Naklejki na urządzenia domowe: pralki, zmywarki, ekspresy, roboty kuchenne",
      "Etykiety informacyjne i ostrzegawcze (gorąca powierzchnia, klasa energetyczna)",
      "Naklejki 3D na małe AGD: blendery, czajniki, tostery, odkurzacze",
    ],
    benefits: [
      "Odporność na temperaturę, wilgoć, parę i detergenty kuchenne",
      "Trwałość w warunkach domowych przez cały okres eksploatacji sprzętu",
      "Wysoka czytelność piktogramów i odporność na ścieranie palcami",
      "Idealne do elementów narażonych na częste dotykanie i czyszczenie",
      "Produkcja w seriach OEM z powtarzalnością koloru między partiami",
      "Łatwa zmiana wersji językowej bez kosztów narzędziowych",
    ],
    keywords: [
      "naklejki 3D AGD",
      "etykiety żywiczne na sprzęt AGD",
      "naklejki wypukłe na urządzenia",
      "doming AGD",
      "naklejki 3D na panele sterowania",
    ],
    ctaLabel: "Wyceń naklejki 3D dla branży AGD",
    ctaHref: "/kalkulator",
  },
  {
    id: "motoryzacja",
    icon: Car,
    h2: "Naklejki 3D dla motoryzacji",
    lead:
      "Naklejki 3D dla motoryzacji to emblematy i etykiety zalewane żywicą poliuretanową z filtrem UV, przystosowane do pracy na zewnątrz pojazdu: odporne na słońce, wodę, myjnie ciśnieniowe, sól drogową i wibracje.",
    body: [
      "Automotive to najbardziej wymagające zastosowanie domingu. Etykieta musi wytrzymać cykl temperaturowy od mrozu do rozgrzanej maski, promieniowanie UV przez cały sezon, chemię myjni oraz mikrodrgania przenoszone z nadwozia. Dlatego do zastosowań motoryzacyjnych stosujemy wyłącznie żywicę poliuretanową z filtrem UV oraz kleje akrylowe o wysokiej odporności termicznej i dobrej przyczepności do lakieru, ABS-u i metalu.",
      "Naklejki żywiczne na samochody sprawdzają się jako emblematy zastępujące kosztowne odlewy: dają wypukłość i głębię zbliżoną do elementów chromowanych, ale są lżejsze, tańsze w krótkich seriach i można je wyprodukować w dowolnym kształcie konturowym. To rozwiązanie chętnie wybierane przez producentów akcesoriów tuningowych, przyczep, quadów i pojazdów specjalnych.",
      "Etykiety 3D automotive stosuje się też wewnątrz pojazdu — na kokpicie, konsoli środkowej, panelach drzwi i elementach wykończeniowych. Wewnątrz kabiny kluczowa jest odporność na wysoką temperaturę za szybą oraz brak żółknięcia, co ponownie przemawia za żywicą poliuretanową.",
    ],
    applications: [
      "Emblematy i logotypy marek motoryzacyjnych oraz warsztatów",
      "Oznaczenia serwisowe, plomby, etykiety przeglądowe i gwarancyjne",
      "Naklejki na części zamienne i akcesoria (filtry, obudowy, sterowniki)",
      "Naklejki na motocykle, quady, skutery i pojazdy terenowe",
      "Etykiety 3D na akcesoria tuningowe i elementy stylizacyjne",
      "Naklejki 3D na elementy wnętrza: kokpit, konsola, panele drzwi",
    ],
    benefits: [
      "Odporność na UV, wodę, sól drogową i mycie ciśnieniowe",
      "Odporność na wibracje i gwałtowne zmiany temperatur",
      "Rozwiązanie przeznaczone do zastosowań outdoor i automotive",
      "Wysoka trwałość (5–7 lat na zewnątrz) w trudnych warunkach",
      "Efekt premium porównywalny z emblematami chromowanymi",
      "Dowolny kształt konturowy bez kosztów form i odlewów",
    ],
    keywords: [
      "naklejki 3D motoryzacja",
      "naklejki żywiczne na samochody",
      "etykiety 3D automotive",
      "doming motoryzacyjny",
      "naklejki wypukłe na części samochodowe",
    ],
    ctaLabel: "Zamów naklejki 3D dla branży motoryzacyjnej",
    ctaHref: "/kalkulator",
  },
  {
    id: "reklama",
    icon: Megaphone,
    h2: "Naklejki 3D dla agencji reklamowych",
    lead:
      "Dla agencji reklamowych i firm zajmujących się gadżetami jesteśmy zapleczem produkcyjnym: realizujemy naklejki 3D dla firm i marek końcowych, z zachowaniem terminów, powtarzalności i pełnej poufności projektu.",
    body: [
      "Agencje reklamowe zamawiają u nas doming najczęściej wtedy, gdy zwykły nadruk na gadżecie nie robi wrażenia na kliencie końcowym. Wypukła naklejka z efektem szkła zmienia tani nośnik w produkt, który klient chce zatrzymać — to działa na powerbankach, pendrive'ach, otwieraczach, breloczkach, kubkach termicznych, kaskach i narzędziach.",
      "Pracujemy w modelu B2B: przyjmujemy pliki wektorowe, przygotowujemy podgląd rozkroju i arkusza, potwierdzamy kolory i realizujemy powtarzalne dosyłki przy kolejnych zamówieniach tego samego klienta. Dzięki temu agencja nie musi za każdym razem odtwarzać ustawień — wracamy do zatwierdzonego wzorca.",
      "Producent naklejek 3D dla reklamy powinien być przewidywalny — dlatego podajemy realny termin już przy wycenie, informujemy o statusie produkcji i wysyłamy zamówienia kurierem na terenie Polski i UE. Dla stałych partnerów prowadzimy uproszczoną ścieżkę zamówień i indywidualne progi cenowe przy większych wolumenach.",
    ],
    applications: [
      "Gadżety reklamowe: powerbanki, pendrive'y, otwieracze, breloki, długopisy",
      "Logotypy na produktach klientów końcowych i produktach własnych agencji",
      "Oznaczenia marek, patronatów, sponsorów i wydarzeń",
      "Produkcja podwykonawcza dla klientów B2B agencji",
      "Naklejki 3D na gadżety premium i zestawy welcome pack",
      "Etykiety 3D do brandingu produktów reklamowych i opakowań",
    ],
    benefits: [
      "Wysoka jakość wykonania i efekt premium widoczny na pierwszy rzut oka",
      "Pełna personalizacja: kształt, kolor, folia, wysokość soczewki",
      "Szybka produkcja i powtarzalność przy dosyłkach",
      "Idealne do brandingu produktów reklamowych i akcesoriów",
      "Produkcja w dużych ilościach z progami rabatowymi",
      "Poufność projektów i praca bez oznaczania produktów naszą marką",
    ],
    keywords: [
      "naklejki 3D dla agencji reklamowych",
      "naklejki wypukłe reklama",
      "etykiety 3D branding",
      "producent naklejek 3D dla reklamy",
      "naklejki 3D na gadżety reklamowe",
    ],
    ctaLabel: "Współpracuj z nami jako agencja reklamowa",
    ctaHref: "/kontakt",
  },
];

const DURABILITY = [
  { icon: Sun, title: "Odporność na UV", text: "Żywica poliuretanowa z filtrem UV nie żółknie i nie matowieje — kolory zostają nasycone przez lata użytkowania na zewnątrz." },
  { icon: Droplets, title: "Odporność na wodę i wilgoć", text: "Zalana soczewka jest szczelna: deszcz, para, mycie ciśnieniowe i kondensacja nie mają dostępu do nadruku." },
  { icon: Thermometer, title: "Odporność termiczna", text: "Naklejki pracują w szerokim zakresie temperatur — od mrozu po rozgrzane obudowy urządzeń i elementy pojazdów." },
  { icon: ShieldCheck, title: "Odporność na chemię i ścieranie", text: "Detergenty, alkohole, oleje i codzienne dotykanie nie uszkadzają nadruku ukrytego pod warstwą żywicy." },
];

const FAQS = [
  {
    q: "Czym są naklejki 3D i jak powstają?",
    a: "Naklejki 3D (nazywane też naklejkami wypukłymi, żywicznymi lub domingiem) to etykiety samoprzylepne, na które po zadrukowaniu i wycięciu nanosi się kroplę przezroczystej żywicy poliuretanowej. Żywica rozlewa się równomiernie do krawędzi i utwardza w formie soczewki, tworząc efekt szkła oraz trwałą warstwę ochronną nad nadrukiem.",
  },
  {
    q: "W jakich branżach najczęściej stosuje się naklejki 3D?",
    a: "Najczęściej w elektronice (logotypy, panele, obudowy), kosmetyce (etykiety premium na słoiki i flakony), AGD (panele sterowania i oznaczenia funkcji), motoryzacji (emblematy, akcesoria, oznaczenia serwisowe) oraz w reklamie i produkcji gadżetów. Coraz częściej także w przemyśle, meblarstwie, sporcie i branży medycznej.",
  },
  {
    q: "Jak długo wytrzymują naklejki żywiczne?",
    a: "W warunkach wewnętrznych praktycznie przez cały cykl życia produktu. Na zewnątrz, przy pełnym nasłonecznieniu i zmiennej pogodzie, standardowo 5–7 lat bez utraty połysku i czytelności — pod warunkiem zastosowania żywicy poliuretanowej z filtrem UV, której używamy w każdej realizacji.",
  },
  {
    q: "Czy naklejki 3D można nakleić na zakrzywione powierzchnie?",
    a: "Tak, w zakresie łagodnych krzywizn — butelek, tub, obudów, kasków czy felg. Przy mocno zakrzywionych powierzchniach dobieramy mniejszy rozmiar etykiety i niższy profil soczewki, aby uniknąć odspajania krawędzi. Przy nietypowym kształcie warto przesłać zdjęcie produktu — dobierzemy wariant.",
  },
  {
    q: "Jaki jest minimalny nakład dla firm?",
    a: "Realizujemy zarówno krótkie serie prototypowe, jak i duże partie produkcyjne OEM. Minimum logistyczne zamówienia to 100 zł netto — całą wycenę można wykonać samodzielnie w kalkulatorze online w 15 sekund, bez logowania i bez kontaktu z handlowcem.",
  },
  {
    q: "Czy naklejki 3D nadają się do kontaktu z kosmetykami i detergentami?",
    a: "Tak. Utwardzona żywica poliuretanowa jest odporna na oleje, alkohole, kremy oraz typowe środki myjące. Zabrudzenia usuwa się wilgotną ściereczką bez ryzyka uszkodzenia nadruku, który znajduje się pod warstwą żywicy.",
  },
  {
    q: "Jakie pliki są potrzebne do produkcji?",
    a: "Najlepiej plik wektorowy (PDF, AI, EPS, SVG) z zamienioną na krzywe typografią i kolorami w CMYK lub Pantone. Przy nadrukach rastrowych wymagamy minimum 300 dpi w skali 1:1. Kształt konturowy zaznaczamy osobną ścieżką — jeśli jej brak, przygotujemy ją po naszej stronie.",
  },
  {
    q: "Czy produkujecie naklejki 3D dla producentów w modelu OEM?",
    a: "Tak. Realizujemy powtarzalne serie dla producentów i marek, w tym produkcję bez oznaczania naszą marką, wersje językowe tego samego wzoru na różne rynki oraz dosyłki na podstawie wcześniej zatwierdzonego wzorca.",
  },
];

const ZastosowaniaBranzowe = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Strona główna", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Zastosowania branżowe naklejek 3D", item: URL },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Produkcja naklejek 3D zalewanych żywicą (doming)",
    name: "Naklejki 3D dla firm — zastosowania branżowe",
    description:
      "Producent naklejek 3D (doming) dla elektroniki, kosmetyków, AGD, motoryzacji i agencji reklamowych. Naklejki żywiczne, etykiety wypukłe premium, serie OEM.",
    provider: {
      "@type": "Organization",
      name: "Naklejki-3d.com.pl",
      url: BASE_URL,
    },
    areaServed: ["PL", "EU"],
    audience: { "@type": "BusinessAudience", audienceType: "Producenci, marki, agencje reklamowe, firmy OEM" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Zastosowania branżowe naklejek 3D",
      itemListElement: INDUSTRIES.map((i) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: i.h2, description: i.lead },
      })),
    },
  };

  return (
    <Layout>
      <SEOHead
        title="Naklejki 3D w branżach: elektronika, AGD, motoryzacja"
        description="Naklejki 3D dla firm: zastosowania w elektronice, kosmetykach, AGD, motoryzacji i reklamie. Etykiety wypukłe premium, doming, serie OEM — wycena online w 15 sekund."
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      {/* Hero */}
      <section className="gradient-navy py-12 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 mb-6">
            <Factory className="w-4 h-4 text-accent" />
            <span className="text-accent text-sm font-medium">Producent naklejek 3D · B2B · Polska</span>
          </div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-5 leading-tight">
            Naklejki 3D – profesjonalne zastosowania w różnych branżach (elektronika, kosmetyki, AGD, motoryzacja, reklama)
          </h1>
          <p className="text-base md:text-xl text-primary-foreground/80 max-w-3xl mx-auto mb-8 leading-relaxed">
            Naklejki żywiczne, etykiety 3D i emblematy zalewane żywicą poliuretanową dla producentów, marek,
            agencji reklamowych i firm OEM. Dowolny kształt, 15 rodzajów folii, produkcja w Polsce.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/kalkulator"
              className="gradient-gold text-accent-foreground font-bold px-8 py-4 rounded-lg hover:opacity-90 transition-opacity shadow-gold inline-flex items-center justify-center gap-2"
            >
              Wycena online w 15 sekund <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/kontakt"
              className="border-2 border-primary-foreground/30 text-primary-foreground font-semibold px-8 py-4 rounded-lg hover:border-accent hover:text-accent transition-colors inline-flex items-center justify-center"
            >
              Zapytaj o ofertę dla swojej branży
            </Link>
          </div>
        </div>
      </section>

      {/* Direct answer for AI search */}
      <section className="py-8 md:py-12 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-secondary/50 border-l-4 border-accent rounded-r-lg p-5 md:p-6">
            <p className="text-foreground text-base md:text-lg leading-relaxed">
              <strong>Naklejki 3D (doming)</strong> to samoprzylepne etykiety wypukłe, w których nadruk zalewany jest
              przezroczystą żywicą poliuretanową tworzącą efekt szkła. Stosuje się je do oznaczania produktów i
              brandingu premium w elektronice, kosmetyce, AGD, motoryzacji, przemyśle i reklamie — wszędzie tam, gdzie
              etykieta musi być jednocześnie efektowna i odporna na UV, wodę, chemię oraz ścieranie.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-8 md:py-14">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Czym są naklejki 3D i dlaczego branże wybierają doming
          </h2>
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
            <p>
              <strong className="text-foreground">Naklejki 3D</strong> — nazywane również{" "}
              <strong className="text-foreground">naklejkami żywicznymi</strong>,{" "}
              <strong className="text-foreground">naklejkami wypukłymi</strong>,{" "}
              <strong className="text-foreground">etykietami 3D</strong> lub{" "}
              <strong className="text-foreground">naklejkami zalewanymi żywicą</strong> — powstają w technologii{" "}
              <strong className="text-foreground">doming</strong>. Zadrukowaną i wyciętą etykietę pokrywa się
              precyzyjnie odmierzoną kroplą dwuskładnikowej żywicy poliuretanowej. Napięcie powierzchniowe rozprowadza
              żywicę równomiernie aż do krawędzi materiału, po czym masa utwardza się w formie wypukłej soczewki. Efekt
              szkła, który powstaje w ten sposób, pogłębia kolory, tworzy grę światła i nadaje płaskiemu nadrukowi
              trójwymiarową głębię.
            </p>
            <p>
              Kluczowa różnica względem zwykłej naklejki nie jest jednak wyłącznie wizualna. Nadruk znajduje się{" "}
              <em>pod</em> żywicą, dzięki czemu jest fizycznie odseparowany od otoczenia. Utwardzona soczewka działa
              jak pancerz: chroni grafikę przed <strong className="text-foreground">promieniowaniem UV</strong> (żywica
              poliuretanowa zawiera filtr i nie żółknie), <strong className="text-foreground">wodą i wilgocią</strong>,{" "}
              <strong className="text-foreground">chemią</strong> — detergentami, alkoholami, olejami, kosmetykami — a
              także przed <strong className="text-foreground">ścieraniem</strong> wynikającym z codziennego dotykania i
              czyszczenia. To dlatego etykiety 3D dla przemysłu stosuje się na produktach pracujących w warunkach
              zewnętrznych, w kuchni, w łazience i w pojazdach.
            </p>
            <p>
              W praktyce marketingowej doming pełni rolę{" "}
              <strong className="text-foreground">brandingu premium</strong>. Wypukłe logo na obudowie urządzenia,
              flakonie perfum czy powerbanku komunikuje jakość, zanim klient przeczyta jakikolwiek opis. Branding z
              naklejkami 3D jest przy tym nieporównywalnie tańszy niż tłoczenie, odlewy metalowe czy grawer — a przy
              krótkich seriach jest praktycznie jedyną opłacalną drogą do uzyskania takiego efektu, ponieważ nie
              wymaga form ani narzędzi.
            </p>
            <p>
              Naszymi odbiorcami są przede wszystkim firmy: <strong className="text-foreground">producenci</strong>{" "}
              oznaczający własne wyroby, <strong className="text-foreground">marki</strong> budujące spójną identyfikację
              na opakowaniach, <strong className="text-foreground">agencje reklamowe</strong> realizujące projekty dla
              klientów końcowych oraz <strong className="text-foreground">firmy OEM i white-label</strong>, które
              potrzebują powtarzalnych serii z gwarancją stałości koloru. Jako{" "}
              <strong className="text-foreground">producent naklejek 3D</strong> prowadzimy cały proces w Polsce — od
              przygotowania pliku i rozkroju, przez druk i wykrawanie, po zalewanie żywicą, kontrolę jakości i wysyłkę.
            </p>
            <p>
              Najczęstsze scenariusze wdrożenia to{" "}
              <strong className="text-foreground">naklejki 3D na produkty</strong> (logotyp, nazwa modelu, wariant
              kolorystyczny), <strong className="text-foreground">etykiety wypukłe premium</strong> na opakowania
              jednostkowe, <strong className="text-foreground">naklejki 3D do oznaczania produktów</strong> numerami
              seryjnymi i partiami, a także <strong className="text-foreground">naklejki 3D dla firm</strong> stosowane
              na gadżetach, flotach pojazdów i wyposażeniu. Poniżej opisujemy pięć branż, w których zastosowania
              naklejek zalewanych żywicą są najlepiej rozpoznane i najczęściej zamawiane.
            </p>
          </div>
        </div>
      </section>

      {/* Durability grid */}
      <section className="py-10 md:py-14 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
            Trwałość naklejek żywicznych — co realnie wytrzymują
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DURABILITY.map((d) => (
              <div key={d.title} className="bg-card border border-border rounded-xl p-5">
                <d.icon className="w-7 h-7 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2">{d.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry navigation */}
      <section className="py-8 md:py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-5">Wybierz swoją branżę</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {INDUSTRIES.map((ind) => (
              <a
                key={ind.id}
                href={`#${ind.id}`}
                className="bg-card border border-border rounded-lg p-4 hover:border-accent transition-colors flex items-center gap-3"
              >
                <ind.icon className="w-5 h-5 text-accent shrink-0" />
                <span className="text-foreground text-sm font-medium capitalize">{ind.id}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Industry sections */}
      {INDUSTRIES.map((ind, idx) => (
        <section
          key={ind.id}
          id={ind.id}
          className={`py-12 md:py-16 scroll-mt-20 ${idx % 2 === 1 ? "bg-secondary/30" : ""}`}
        >
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-xl gradient-gold flex items-center justify-center shrink-0">
                <ind.icon className="w-6 h-6 text-accent-foreground" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">{ind.h2}</h2>
            </div>

            <p className="text-foreground text-base md:text-lg leading-relaxed mb-5 border-l-4 border-accent pl-4">
              {ind.lead}
            </p>

            <div className="space-y-4 mb-8">
              {ind.body.map((p, i) => (
                <p key={i} className="text-muted-foreground text-base md:text-lg leading-relaxed">
                  {p}
                </p>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3">Zastosowania</h3>
                <ul className="space-y-2">
                  {ind.applications.map((a) => (
                    <li key={a} className="flex items-start gap-2 text-muted-foreground text-sm md:text-base">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-1" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3">Korzyści</h3>
                <ul className="space-y-2">
                  {ind.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-muted-foreground text-sm md:text-base">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-1" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-2 uppercase tracking-wide">
                Powiązane frazy
              </h3>
              <div className="flex flex-wrap gap-2">
                {ind.keywords.map((k) => (
                  <span
                    key={k}
                    className="text-xs md:text-sm bg-secondary text-muted-foreground border border-border rounded-full px-3 py-1"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>

            <Link
              to={ind.ctaHref}
              className="gradient-gold text-accent-foreground font-bold px-6 py-3.5 rounded-lg hover:opacity-90 transition-opacity shadow-gold inline-flex items-center gap-2"
            >
              {ind.ctaLabel} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      ))}

      {/* Other industries / long tail */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Inne branże, w których sprawdzają się etykiety 3D
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
            Doming nie ogranicza się do pięciu opisanych sektorów. Naklejki zalewane żywicą stosuje się wszędzie tam,
            gdzie oznaczenie ma przetrwać eksploatację i jednocześnie wyglądać jak element wykończenia produktu:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Przemysł i automatyka — tabliczki znamionowe, oznaczenia maszyn, panele operatorskie",
              "Meblarstwo — logotypy producentów mebli, oznaczenia kolekcji i serii",
              "Sport i outdoor — naklejki na rowery, kaski, deski, sprzęt siłowy",
              "Branża medyczna i laboratoryjna — oznaczenia aparatury odporne na dezynfekcję",
              "Narzędzia i elektronarzędzia — logotypy odporne na uderzenia i smary",
              "Nieruchomości i wyposażenie — numeracja, oznaczenia wind, tabliczki informacyjne",
              "Gastronomia — oznaczenia ekspresów, dystrybutorów i sprzętu barowego",
              "Energetyka i fotowoltaika — etykiety inwerterów i skrzynek odpornych na UV",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2 bg-card border border-border rounded-lg p-4">
                <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-1" />
                <span className="text-muted-foreground text-sm md:text-base">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to order */}
      <section className="py-12 md:py-16 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
            Jak zamówić naklejki 3D dla swojej branży — krok po kroku
          </h2>
          <ol className="space-y-4">
            {[
              { n: "Określ zastosowanie i warunki pracy", t: "Powiedz nam, na czym etykieta będzie naklejona i gdzie będzie pracować (wnętrze, zewnątrz, kontakt z chemią). Na tej podstawie dobieramy folię, klej i profil soczewki." },
              { n: "Wyceń online w kalkulatorze", t: "Wpisz wymiary, kształt, rodzaj folii i nakład — kalkulator poda cenę w 15 sekund, bez logowania. Minimum logistyczne zamówienia to 100 zł netto." },
              { n: "Prześlij plik graficzny", t: "Najlepiej wektor (PDF, AI, EPS, SVG) w skali 1:1, z krzywymi i kolorami CMYK/Pantone. Grafikę rastrową przyjmujemy w minimum 300 dpi." },
              { n: "Zatwierdź podgląd produkcyjny", t: "Przygotowujemy podgląd rozkroju i kształtu wykrojnika. Po akceptacji wzorzec zapisujemy — kolejne dosyłki są w pełni powtarzalne." },
              { n: "Produkcja i wysyłka", t: "Druk, wykrawanie, zalewanie żywicą i utwardzanie, kontrola jakości, pakowanie. Wysyłka kurierem na terenie Polski i UE." },
            ].map((s, i) => (
              <li key={s.n} className="flex gap-4 bg-card border border-border rounded-lg p-5">
                <div className="shrink-0 w-10 h-10 rounded-full gradient-gold flex items-center justify-center text-accent-foreground font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{s.n}</h3>
                  <p className="text-muted-foreground text-sm md:text-base leading-relaxed">{s.t}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
            Najczęściej zadawane pytania o zastosowania naklejek 3D
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq) => (
              <details key={faq.q} className="bg-card border border-border rounded-xl overflow-hidden group">
                <summary className="p-5 font-semibold text-foreground cursor-pointer hover:bg-secondary/50 transition-colors list-none flex items-center justify-between gap-3">
                  <span>{faq.q}</span>
                  <span className="text-accent group-open:rotate-45 transition-transform text-2xl leading-none shrink-0">+</span>
                </summary>
                <div className="px-5 pb-5 text-muted-foreground leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-10 md:py-14 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-5">Zobacz również</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { to: "/naklejki-doming", label: "Naklejki doming — technologia i proces produkcji" },
              { to: "/naklejki-wypukle", label: "Naklejki wypukłe 3D — rodzaje folii i wykończeń" },
              { to: "/naklejki-z-logo", label: "Naklejki 3D z logo firmy" },
              { to: "/produkty", label: "Katalog produktów i 15 rodzajów folii" },
              { to: "/realizacje", label: "Przykładowe realizacje — wideo" },
              { to: "/blog/jak-sie-robi-naklejki-3d", label: "Jak się robi naklejki 3D? Proces krok po kroku" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="bg-card border border-border rounded-lg p-4 hover:border-accent transition-colors text-foreground text-sm md:text-base inline-flex items-center justify-between gap-2"
              >
                {l.label} <ArrowRight className="w-4 h-4 text-accent shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 gradient-navy">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-primary-foreground mb-4">
            Potrzebujesz naklejek 3D dla swojej branży?
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-base md:text-lg">
            Wyceń nakład online w 15 sekund albo opisz nam swój produkt — dobierzemy folię, klej i profil żywicy
            do warunków, w jakich etykieta będzie pracować.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/kalkulator"
              className="gradient-gold text-accent-foreground font-bold px-10 py-4 rounded-lg hover:opacity-90 transition-opacity shadow-gold inline-flex items-center justify-center gap-2 text-lg"
            >
              Wyceń teraz <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/kontakt"
              className="border-2 border-primary-foreground/30 text-primary-foreground font-semibold px-10 py-4 rounded-lg hover:border-accent hover:text-accent transition-colors inline-flex items-center justify-center text-lg"
            >
              Zapytanie B2B
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ZastosowaniaBranzowe;
